import { BulkBpProfileCreateInput, BulkBpProfileUpdateInput } from '@bp/bp-graphql-types';
import { ProfileRoles } from '@bp/pim-auth-constants';
import { SliderModal } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { ClassesListItemQueryFieldsDocument } from 'components/Classes/ClassesListItem';
import { BpClassType } from 'pages/Institution/subpages/InstitutionClassesSubpage';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useConfirm } from '../../../hooks/useConfirm';
import { createForeignRefId, SyncMaster } from '../../../utils/create-foreignRefId';
import { toBirthdayDate } from '../../../utils/dateCalculations';
import { getGradeAndGradeGroupFromString } from '../../../utils/gradesHelper';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';
import { showUserDbErrorToast } from '../../../utils/toasts';
import { XlsxImportDropper } from '../components/XlsxImportDropper';
import { XlsxImportOverview } from '../components/XlsxImportOverview';
import { XlsxStudentImportForm } from '../forms/XlsxStudentImportForm';
import { XlsxStudentMappingForm } from '../forms/XlsxStudentMappingForm';
import {
  mandatoryStudentMappingOptions,
  StudentMapping,
  studentMappingOptions,
  XlsxFileImport,
  XlsxImportColumn,
  XlsxImportEntry,
} from '../types/XlsxImportTypes';
import { getLabelFromMapping, studentSortFn } from '../utils/XlsxImportUtils';
import styles from './_ImportModals.module.scss';

const BulkUpdateOrCreateBpProfilesMutation = graphql(`
  mutation BulkUpdateOrCreateBpProfiles($input: [BulkBpProfilesUpdateOrCreateInput!]!, $organization: ID!) {
    bulkUpdateOrCreateBpProfiles(input: $input, organization: $organization) {
      created {
        __typename
        uuid
        firstName
        lastName
      }
      updated {
        __typename
        uuid
        firstName
        lastName
      }
      ignored {
        __typename
        firstName
        lastName
      }
    }
  }
`);

type XlsxStudentImportModalProps = {
  isOpen: boolean;
  classes: BpClassType[];
  onClose: () => void;
  query: FragmentType<typeof ClassesListItemQueryFieldsDocument> | undefined;
};

export const XlsxStudentImportModal = ({ isOpen, classes, onClose, query }: XlsxStudentImportModalProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const fragment = getFragmentData(ClassesListItemQueryFieldsDocument, query);

  const students = useMemo(
    () => fragment?.profiles.filter((s) => s.organizationRoles.includes(ProfileRoles.Student)) ?? [],
    [fragment?.profiles],
  );

  const [{ error: importError }, bulkUpdateOrCreateBpProfiles] = useMutation(BulkUpdateOrCreateBpProfilesMutation);

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingLabel, setLoadingLabel] = useState<string>('');
  const [index, setIndex] = useState<number>(0);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [dirty, setDirty] = useState<boolean>(false);
  const [fileImport, setFileImport] = useState<XlsxFileImport | null>(null);
  const [columns, setColumns] = useState<XlsxImportColumn<StudentMapping>[]>([]);
  const [entries, setEntries] = useState<XlsxImportEntry<StudentMapping>[]>([]);

  importError && showErrorToast(importError.message);

  const { ConfirmationDialog, confirm } = useConfirm({
    defaultTitle: t('confirm.abort.title'),
    defaultConfirmText: t('confirm.abort.confirm'),
    defaultMessage: t('confirm.abort.message'),
    defaultCancelText: t('confirm.abort.cancel'),
  });

  async function handleClose() {
    if (dirty) {
      const result = await confirm();
      if (result) reset();
    } else {
      reset();
    }
  }

  function reset() {
    setCurrentIndex(null);
    setIndex(0);
    setFileImport(null);
    setDirty(false);
    onClose();
  }

  const handleFileLoaded = useCallback(
    (fileImport: XlsxFileImport) => {
      const initialColumns: XlsxImportColumn<StudentMapping>[] = fileImport
        ? studentMappingOptions.map((column) => {
            const translatedLabel = getLabelFromMapping(column);
            const index = fileImport.headers.findIndex((h) => h === translatedLabel);
            return {
              content: column,
              index: index !== undefined && index > -1 ? index : null,
            };
          })
        : [];

      const initialEntries: XlsxImportEntry<StudentMapping>[] = fileImport.rows.map((row, index) => {
        // new entry object with values from personMappingOptions as keys
        const entry: Record<StudentMapping, string | null> = studentMappingOptions.reduce(
          (acc, option) => ({ ...acc, [option]: null }),
          {} as Record<StudentMapping, string | null>,
        );

        // iterate personMappingOptions and find value from row for each entry
        studentMappingOptions.forEach((option) => {
          const index = initialColumns.find(({ content }) => content === option)?.index ?? null;
          entry[option] = index || index === 0 ? row[index] : null;
        });

        // find already existing person
        const selectedProfile = students.find((p) => p.displayName === `${entry.firstName} ${entry.lastName}`);

        return { ...entry, rowIndex: index, connectProfileUuid: selectedProfile?.uuid ?? null, ignore: [] };
      });

      setColumns(initialColumns);
      setEntries(initialEntries.sort(studentSortFn));
      setFileImport(fileImport);
      setCurrentIndex(1);
      setIndex(1);
    },
    [students],
  );

  // slider components
  const FileUpload: ReactNode = useMemo(() => {
    return (
      <div className={styles.slide}>
        <XlsxImportDropper onLoad={setLoading} onFileLoaded={handleFileLoaded} className={styles.dropper} />
      </div>
    );
  }, [handleFileLoaded]);

  const handleLoadEntriesFromFile = (columns: XlsxImportColumn<StudentMapping>[], fileImport: XlsxFileImport) => {
    setLoading(true);
    const newEntries: XlsxImportEntry<StudentMapping>[] = fileImport.rows.map((row, rowIndex) => {
      const ignoreReasons: StudentMapping[] = [];
      let grade: string | null = null;
      let gradeGroup: string | null = null;
      let gradeAndGradeGroup: string | null = null;

      // new entry object with values from personMappingOptions as keys
      const entry = studentMappingOptions.reduce(
        (acc, option) => ({ ...acc, [option]: null }),
        {} as Record<StudentMapping, string | null>,
      );

      // iterate personMappingOptions and find value from row for each entry
      studentMappingOptions.forEach((option) => {
        const index = columns.find(({ content }) => content === option)?.index ?? null;
        const value = index === null ? null : row[index];
        entry[option] = value;

        // if a mandatory field has no value, add entry to ignore-array
        if (option === 'firstName' && value === null) ignoreReasons.push(option);
        if (option === 'lastName' && value === null) ignoreReasons.push(option);
        if (option === 'foreignRefId' && value === null) ignoreReasons.push(option);

        // get class data
        if (option === 'grade') grade = value;
        if (option === 'gradeGroup') gradeGroup = value;
        if (option === 'gradeAndGradeGroup') gradeAndGradeGroup = value;
      });

      // map classes
      const classToConnect = classes.find((c) => {
        if (grade && gradeGroup) {
          return isNaN(Number(grade)) ? false : c.grade === Number(grade) && c.gradeGroup === gradeGroup;
        } else if (grade) {
          return isNaN(Number(grade)) ? false : c.grade === Number(grade) && c.gradeGroup === '';
        } else if (gradeAndGradeGroup) {
          const { grade, gradeGroup } = getGradeAndGradeGroupFromString(gradeAndGradeGroup);
          return c.grade === grade && c.gradeGroup === gradeGroup;
        }
        return false;
      });

      if (!classToConnect) {
        ignoreReasons.push('grade');
      } else {
        entry.classUuid = classToConnect.uuid;
      }

      // find already existing person
      const selectedProfile = students.find(
        (p) =>
          p.displayName === `${entry.firstName} ${entry.lastName}` ||
          (p.firstName === entry.firstName && p.lastName === entry.lastName) ||
          p.foreignRefId?.includes(createForeignRefId(SyncMaster.excel, entry.foreignRefId ?? '')),
      );

      return {
        ...entry,
        rowIndex,
        connectProfileUuid: selectedProfile?.uuid ?? null,
        ignore: ignoreReasons,
      };
    });

    setEntries(newEntries.sort(studentSortFn));
    setDirty(true);
    setLoading(false);
  };

  const ImportForm: ReactNode = useMemo(() => {
    const handleColumnChange = (columns: XlsxImportColumn<StudentMapping>[]) => {
      setColumns(columns);
    };

    return (
      fileImport && (
        <div className={styles.slide}>
          <XlsxStudentImportForm
            fileImport={fileImport}
            initialColumns={columns}
            onColumnsChanged={(columns) => {
              handleColumnChange(columns);
            }}
          />
        </div>
      )
    );
  }, [fileImport, columns]);

  const ImportOverview: ReactNode = useMemo(() => {
    return (
      <div className={styles.slide}>
        <XlsxImportOverview<StudentMapping>
          entries={entries}
          mandatoryFields={mandatoryStudentMappingOptions}
          notFoundFields={['grade']}
        />
      </div>
    );
  }, [entries]);

  const MappingForm: ReactNode = useMemo(() => {
    const newEntries = entries.filter(
      (e) => (e.connectProfileUuid === null || e.connectProfileUuid === '') && e.ignore.length === 0,
    );
    const matchedEntries = entries.filter((e) => e.connectProfileUuid && e.ignore.length === 0);
    return (
      <div className={styles.slide}>
        <XlsxStudentMappingForm
          students={students.map((s) => ({ uuid: s.uuid, name: s.displayName ?? '' }))}
          newEntries={newEntries}
          matchedEntries={matchedEntries}
          onEntryChanged={(entry) => {
            // add enty and sort again
            const updatedEntries = entries.map((e) => (e.rowIndex === entry.rowIndex ? entry : e));
            setEntries(updatedEntries.sort(studentSortFn));
          }}
        />
      </div>
    );
  }, [entries, students]);

  // Pflichtfelder
  const missingColumns = useMemo(() => {
    return columns.filter((c) => mandatoryStudentMappingOptions.includes(c.content) && c.index === null);
  }, [columns]);

  const MissingColumnsHint: ReactNode | undefined =
    missingColumns.length > 0 ? (
      <div style={{ color: 'var(--color-error)' }}>
        {t('import.missingColumnsHint', {
          columns: missingColumns.map((c) => getLabelFromMapping(c.content)).join(', '),
        })}
      </div>
    ) : undefined;

  // next/previous logic
  const canViewImportForm = !!fileImport;

  const canViewOverview = missingColumns.length === 0;

  const nextDisabled = index === 0 ? !canViewImportForm : index === 1 ? !canViewOverview : false;

  // submit
  async function handleSubmit() {
    setLoading(true);
    setLoadingLabel(t('import.importStartedInfo'));

    const convertEntryToInput = (
      entry: XlsxImportEntry<StudentMapping>,
      operation: 'update' | 'create',
    ): BulkBpProfileCreateInput | BulkBpProfileUpdateInput => {
      return {
        salutation: entry.salutation ?? undefined,
        title: entry.title ?? undefined,
        lastName: entry.lastName ?? '',
        firstName: entry.firstName ?? '',
        firstNames: entry.firstNames ?? undefined,
        prefixName: entry.prefixName ?? undefined,
        shortName: entry.shortName ?? undefined,
        email: entry.email ?? undefined,
        birthday: entry.birthday ? toBirthdayDate(entry.birthday) : undefined,
        address1: entry.address1 ?? undefined,
        // we import students here, so this has to be STUDENT
        organizationRoles: [ProfileRoles.Student],
        foreignRefId: entry.foreignRefId?.toString() ?? '',
        grade: entry.grade?.toString() ?? undefined,
        gradeGroup: entry.gradeGroup ?? undefined,
        gradeAndGradeGroup: entry.gradeAndGradeGroup ?? undefined,
        profileUuid: operation === 'update' ? (entry.connectProfileUuid ?? undefined) : undefined,
        classUuid: entry.classUuid ?? undefined,
      };
    };

    const validEntries = entries.filter((e) => e.ignore.length === 0);
    const updateProfiles = validEntries.filter((e) => e.connectProfileUuid !== null);
    const createProfiles = validEntries.filter((e) => e.connectProfileUuid === null);

    const { error, data } = await bulkUpdateOrCreateBpProfiles({
      input: [
        ...updateProfiles.map((entry) => {
          return {
            update: convertEntryToInput(entry, 'update') as BulkBpProfileUpdateInput,
          };
        }),
        ...createProfiles.map((entry) => ({
          create: convertEntryToInput(entry, 'create') as BulkBpProfileCreateInput,
        })),
      ],
      organization: organizationUuid,
    });

    if (error) {
      showUserDbErrorToast();
    } else {
      const ignored = data?.bulkUpdateOrCreateBpProfiles?.ignored ?? [];
      showSuccessToast(
        ignored.length > 0
          ? t('import.importSuccessWithIgnored', { count: ignored.length })
          : t('import.importSuccess'),
      );
    }

    setLoading(false);
    setLoadingLabel('');

    reset();
  }

  return (
    <>
      <SliderModal
        className={styles['import-modal']}
        isOpen={isOpen}
        loading={loading}
        loadingLabel={loadingLabel}
        title={t('import.importType', { type: t('students.title', { count: 2 }) })}
        submitText={t('import.import')}
        modalSettings={{ width: 'l' }}
        slides={[FileUpload, ImportForm, ImportOverview, MappingForm]}
        defaultIndex={currentIndex ?? undefined}
        onClose={handleClose}
        onNext={() => {
          if (fileImport && index === 1) {
            handleLoadEntriesFromFile(columns, fileImport);
          }
          setIndex(index + 1);
        }}
        onPrevious={() => setIndex(index - 1)}
        nextDisabled={nextDisabled}
        submitDisabled={index !== 3 || !canViewOverview}
        onSubmit={handleSubmit}
        additionalLeftComponent={index === 1 && MissingColumnsHint}
      />

      <ConfirmationDialog />
    </>
  );
};
